// Plausible analytics — privacy-friendly pageview tracking for the site.
//
// Inert-by-default: unset `VITE_PLAUSIBLE_DOMAIN` skips the script
// injection entirely (no network request, no cookies, nothing in the
// bundle's runtime behavior). Matches the Sentry pattern next door in
// lib/sentry.ts.
//
// Plausible tracks pageviews + SPA navigations automatically when the
// `script.js` variant is loaded; `data-domain` tells the script which
// Plausible site to report to. No cookies, no personal data — Plausible's
// default script is GDPR-friendly out of the box.
//
// We inject the script from TS (not from index.html) so the tag is only
// present when an operator opts in. Hardcoding it in index.html and
// gating via an env var would still ship the script URL literal in the
// output HTML — the inject-on-demand path keeps the "off" state fully
// clean.

import { attributionProps } from "./attribution";

type PlausibleProxyEventFn = (
  event: string,
  options?: { callback?: () => void; props?: Record<string, string | number | boolean> },
) => void;

type PlausibleEventProps = Record<string, string | number | boolean>;

declare global {
  interface Window {
    plausible?: PlausibleProxyEventFn;
  }
}

const SCRIPT_ID = "plausible-script";

export const M1_PLAUSIBLE_EVENTS = {
  launchVisit: "launch_visit",
  launchSignup: "launch_signup",
  launchActivation: "launch_activation",
  launchPaid: "launch_paid",
  signupFree: "signup_free",
  signupPro: "signup_pro",
  checkoutStarted: "checkout_started",
  checkoutCompleted: "checkout_completed",
} as const;

export const SEO_FUNNEL_EVENTS = {
  downloadClick: "download_click",
  signupClick: "signup_click",
  pricingCtaClick: "pricing_cta_click",
  checkoutStarted: M1_PLAUSIBLE_EVENTS.checkoutStarted,
  trialActivated: "trial_activated",
  paywallHit: "paywall_hit",
} as const;

export type SeoFunnelProps = {
  source_page?: string;
  lang?: string;
  plan?: string;
  os?: string;
  billing_cycle?: string;
} & PlausibleEventProps;

const CALLBACK_FALLBACK_MS = 200;

export function initPlausible(): void {
  const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
  if (!domain) return;

  // `VITE_PLAUSIBLE_SRC` lets operators point at a self-hosted deployment
  // or Plausible's custom-domain proxy (ad blockers block `plausible.io`
  // by default; proxies dodge that). Falls back to the standard CDN.
  const src = import.meta.env.VITE_PLAUSIBLE_SRC || "https://plausible.io/js/script.js";

  // Bail if another tab/component already injected the script — React
  // StrictMode double-invokes effects in dev, and HMR reruns module code.
  if (document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.defer = true;
  script.src = src;
  script.dataset.domain = domain;
  document.head.appendChild(script);
}

export function trackPlausibleEvent(event: string, props?: PlausibleEventProps): void {
  if (typeof window === "undefined") return;
  window.plausible?.(event, props ? { props } : undefined);
}

// Public helper used by feature code. Alias kept for readability in callsites.
export function trackGoal(name: string, props?: PlausibleEventProps): void {
  trackPlausibleEvent(name, props);
}

export function trackDownloadClick(props: SeoFunnelProps): void {
  trackPlausibleEvent(SEO_FUNNEL_EVENTS.downloadClick, withAttribution(props));
}

export function trackSignupClick(props: SeoFunnelProps): void {
  trackPlausibleEvent(SEO_FUNNEL_EVENTS.signupClick, withAttribution(props));
}

export function trackPricingCtaClick(props: SeoFunnelProps): void {
  trackPlausibleEvent(SEO_FUNNEL_EVENTS.pricingCtaClick, withAttribution(props));
}

export function trackCheckoutStartedBeforeRedirect(
  onComplete: () => void,
  props?: SeoFunnelProps,
): void {
  trackPlausibleEventBeforeRedirect(
    SEO_FUNNEL_EVENTS.checkoutStarted,
    onComplete,
    withAttribution(props),
  );
}

// Placeholders for desktop lifecycle hooks. The desktop renderer will call
// these once it can report first-transcription activation and paywall views.
export function trackTrialActivated(props?: SeoFunnelProps): void {
  trackPlausibleEvent(SEO_FUNNEL_EVENTS.trialActivated, withAttribution(props));
}

export function trackPaywallHit(props?: SeoFunnelProps): void {
  trackPlausibleEvent(SEO_FUNNEL_EVENTS.paywallHit, withAttribution(props));
}

export function sourcePageFromPath(pathname: string): string {
  const normalized = pathname.replace(/^\/(pt|es)(?=\/|$)/, "").replace(/^\/+|\/+$/g, "");
  return normalized || "home";
}

export function currentSourcePage(): string {
  if (typeof window === "undefined") return "unknown";
  return sourcePageFromPath(window.location.pathname);
}

/**
 * Track an event before an outbound redirect. If Plausible is blocked or
 * absent, run immediately. If callback fails, fallback timer preserves UX.
 */
export function trackPlausibleEventBeforeRedirect(
  event: string,
  onComplete: () => void,
  props?: PlausibleEventProps,
): void {
  if (typeof window === "undefined" || !window.plausible) {
    onComplete();
    return;
  }

  let completed = false;
  const finish = () => {
    if (completed) return;
    completed = true;
    onComplete();
  };

  window.plausible(event, { callback: finish, props });
  window.setTimeout(finish, CALLBACK_FALLBACK_MS);
}

function withAttribution(props?: PlausibleEventProps): PlausibleEventProps {
  return { ...attributionProps(), ...(props ?? {}) };
}
